import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableToken,
  useCollectionBinding,
  useContentToken,
  useProperty,
  usePropertyField,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
}

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
}

const SLIDE_INTERVAL = 5000;
const PAGE_SIZE = 10;

const COMMUNITY_KEYS = ['community', 'property', 'exterior', 'common area', 'site amenity'];
const APARTMENT_KEYS = ['apartment', 'unit', 'interior', 'in home', 'home feature'];
const COMMUNITY_PHOTO_KEYS = ['community', 'amenity', 'pool', 'clubhouse', 'fitness', 'gym', 'courtyard', 'lounge'];
const APARTMENT_PHOTO_KEYS = ['apartment', 'unit', 'interior', 'home', 'kitchen', 'bedroom', 'bath'];

function parseAmenityList(raw: string | null | undefined): Amenity[] {
  if (!raw) return [];
  return raw
    .split(/[\n;,•|]+/)
    .map((entry) => entry.replace(/^[-–—*]\s*/, '').trim())
    .filter(Boolean)
    .map((title) => ({ title }));
}

function normalize(value: string | null | undefined): string {
  return (value ?? '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function matchesKeys(value: string | null | undefined, keys: string[]): boolean {
  const normalized = normalize(value);
  return !!normalized && keys.some((key) => normalized.includes(key));
}

function isApartmentGroup(group: string | null | undefined): boolean {
  const normalized = normalize(group);
  return matchesKeys(normalized, APARTMENT_KEYS) && !normalized.includes('community');
}

function isCommunityGroup(group: string | null | undefined): boolean {
  return matchesKeys(group, COMMUNITY_KEYS);
}

function photoHaystack(photo: GalleryPhoto): string {
  return `${photo.title ?? ''} ${photo.alt_text ?? ''} ${photo.description ?? ''}`;
}

function photosMatching(photos: GalleryPhoto[], keywords: string[]): GalleryPhoto[] {
  return photos.filter((photo) => matchesKeys(photoHaystack(photo), keywords));
}

function photosFromAmenities(items: Amenity[]): GalleryPhoto[] {
  return items
    .filter((item) => item.amenity_image)
    .map((item) => ({
      image: item.amenity_image,
      alt_text: item.amenity_image_alt_text,
      title: item.title,
    }));
}

function photoId(photo: GalleryPhoto): string {
  return photo.image || photo.original_url || photo.title || '';
}

function photoUrl(photo: GalleryPhoto): string {
  return photo.image || photo.original_url || '';
}

function classifyAmenities(items: Amenity[]): {
  community: Amenity[];
  apartment: Amenity[];
  other: Amenity[];
} {
  const community: Amenity[] = [];
  const apartment: Amenity[] = [];
  const other: Amenity[] = [];

  for (const item of items) {
    if (isApartmentGroup(item.group)) apartment.push(item);
    else if (isCommunityGroup(item.group)) community.push(item);
    else other.push(item);
  }

  return { community, apartment, other };
}

function resolveAmenityColumns(
  items: Amenity[],
  communityList: Amenity[],
  apartmentList: Amenity[],
): { community: Amenity[]; apartment: Amenity[] } {
  const { community, apartment, other } = classifyAmenities(items);

  return {
    community: [
      ...(community.length > 0 ? community : communityList),
      ...other,
    ],
    apartment: apartment.length > 0 ? apartment : apartmentList,
  };
}

function resolveColumnPhotos(
  bound: GalleryPhoto[],
  allPhotos: GalleryPhoto[],
  keywords: string[],
  items: Amenity[],
  usedIds: Set<string>,
  fallbackToUnusedGallery: boolean,
): GalleryPhoto[] {
  const usableBound = bound.filter((photo) => photo.image || photo.original_url);
  if (usableBound.length > 0) return usableBound;

  const matched = photosMatching(allPhotos, keywords).filter((photo) => !usedIds.has(photoId(photo)));
  if (matched.length > 0) return matched;

  const fromItems = photosFromAmenities(items);
  if (fromItems.length > 0) return fromItems;

  if (!fallbackToUnusedGallery) return [];

  return allPhotos
    .filter((photo) => (photo.image || photo.original_url) && !usedIds.has(photoId(photo)))
    .slice(0, 8);
}

function AmenityCarousel({ photos, label }: { photos: GalleryPhoto[]; label: string }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const visiblePhotos = photos.filter((photo) => photoUrl(photo));
  const count = visiblePhotos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent((prev) => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    setCurrent((prev) => (count === 0 ? 0 : prev % count));
  }, [count]);

  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const rotating = count > 1 && !paused && !reducedMotion;

  useEffect(() => {
    if (!rotating) return;
    const timer = window.setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => window.clearInterval(timer);
  }, [rotating, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div aria-live={rotating ? 'off' : 'polite'}>
        {visiblePhotos.map((photo, index) => {
          const active = index === current;
          return (
            <div
              key={photoUrl(photo) || index}
              className={`amenities-carousel-img${active ? ' amenities-carousel-img--active' : ''}`}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${count}`}
              aria-hidden={!active}
            >
              <VibesitesImage
                url={photoUrl(photo)}
                alt={photo.alt_text || photo.title || label}
                className="h-full w-full object-cover"
                sizes="(min-width: 992px) 58vw, 100vw"
                priority={index === 0}
              />
            </div>
          );
        })}
      </div>

      {count > 1 && (
        <>
          <button type="button" className="amenities-carousel-arrow amenities-carousel-arrow--prev" onClick={() => advance(-1)} aria-label="Previous photo">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button type="button" className="amenities-carousel-arrow amenities-carousel-arrow--next" onClick={() => advance(1)} aria-label="Next photo">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}

      {count > 1 && (
        <div className="amenities-carousel-dots">
          {visiblePhotos.map((photo, index) => (
            <button
              key={photoUrl(photo) || index}
              type="button"
              className={`amenities-carousel-dot${index === current ? ' amenities-carousel-dot--active' : ''}`}
              onClick={() => setCurrent(index)}
              aria-label={`Photo ${index + 1} of ${count}`}
              aria-current={index === current ? 'true' : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface AmenityBandProps {
  heading: ReactNode;
  items: Amenity[];
  itemsBinding: 'amenitieslist.communityItems' | 'amenitieslist.apartmentItems';
  itemsLabel: string;
  photos: GalleryPhoto[];
  photosBinding: 'amenitieslist.communityPhotos' | 'amenitieslist.apartmentPhotos';
  photosLabel: string;
  flipped: boolean;
}

function AmenityBand({
  heading,
  items,
  itemsBinding,
  itemsLabel,
  photos,
  photosBinding,
  photosLabel,
  flipped,
}: AmenityBandProps) {
  const bandRef = useScrollReveal<HTMLDivElement>();
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const list = (
    <ul className="amenities-name-list">
      {pageItems.map((item, index) => (
        <li key={`${page}-${item.title || index}`} className="amenities-name-item">
          <span className="amenities-name-mark" aria-hidden="true" />
          <span>{item.title || ''}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div ref={bandRef} className={`amenities-band reveal-fade-up${flipped ? ' amenities-band--flip' : ''}`}>
      {photosBinding === 'amenitieslist.communityPhotos' ? (
        <EditableCollectionBinding bindingPath="amenitieslist.communityPhotos" label={photosLabel} className="amenities-band-media">
          <AmenityCarousel photos={photos} label={photosLabel} />
        </EditableCollectionBinding>
      ) : (
        <EditableCollectionBinding bindingPath="amenitieslist.apartmentPhotos" label={photosLabel} className="amenities-band-media">
          <AmenityCarousel photos={photos} label={photosLabel} />
        </EditableCollectionBinding>
      )}

      <div className="amenities-band-copy">
        <div className="amenities-band-copy-inner">
          {heading}
          {itemsBinding === 'amenitieslist.communityItems' ? (
            <EditableCollectionBinding bindingPath="amenitieslist.communityItems" label={itemsLabel}>
              {list}
            </EditableCollectionBinding>
          ) : (
            <EditableCollectionBinding bindingPath="amenitieslist.apartmentItems" label={itemsLabel}>
              {list}
            </EditableCollectionBinding>
          )}
          {totalPages > 1 && (
            <div className="amenities-pagination">
              <button type="button" className="amenities-page-btn" onClick={() => setPage((prev) => Math.max(0, prev - 1))} disabled={page === 0} aria-label="Previous page">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <div className="amenities-page-dots">
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`amenities-page-dot${index === page ? ' amenities-page-dot--active' : ''}`}
                    onClick={() => setPage(index)}
                    aria-label={`Page ${index + 1}`}
                    aria-current={index === page ? 'page' : undefined}
                  />
                ))}
              </div>
              <button type="button" className="amenities-page-btn" onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))} disabled={page === totalPages - 1} aria-label="Next page">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AmenitiesList() {
  const communityHeading = useContentToken('amenitieslist.communityHeading');
  const apartmentHeading = useContentToken('amenitieslist.apartmentHeading');

  const communityBound = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });
  const apartmentBound = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });
  const communityPhotosBound = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });
  const apartmentPhotosBound = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  const property = useProperty();
  const communityListRaw = usePropertyField('property_community_amenities_list');
  const apartmentListRaw = usePropertyField('property_apartment_features_list');

  const { communityItems, apartmentItems, communityPhotos, apartmentPhotos } = useMemo(() => {
    const allAmenities = ((property.amenities ?? []) as Amenity[]).filter((item) => item.title || item.description);
    const allPhotos = (property.photo_gallery ?? []) as GalleryPhoto[];
    const resolved = resolveAmenityColumns(
      allAmenities,
      parseAmenityList(communityListRaw),
      parseAmenityList(apartmentListRaw),
    );

    const community = communityBound.length > 0 ? communityBound : resolved.community;
    const apartment = apartmentBound.length > 0 ? apartmentBound : resolved.apartment;

    const usedPhotoIds = new Set<string>();
    const nextCommunityPhotos = resolveColumnPhotos(
      communityPhotosBound,
      allPhotos,
      COMMUNITY_PHOTO_KEYS,
      community,
      usedPhotoIds,
      true,
    );
    nextCommunityPhotos.forEach((photo) => usedPhotoIds.add(photoId(photo)));
    const nextApartmentPhotos = apartment.length > 0
      ? resolveColumnPhotos(
          apartmentPhotosBound,
          allPhotos,
          APARTMENT_PHOTO_KEYS,
          apartment,
          usedPhotoIds,
          false,
        )
      : [];

    return {
      communityItems: community,
      apartmentItems: apartment,
      communityPhotos: nextCommunityPhotos,
      apartmentPhotos: nextApartmentPhotos,
    };
  }, [
    property.amenities,
    property.photo_gallery,
    communityBound,
    apartmentBound,
    communityPhotosBound,
    apartmentPhotosBound,
    communityListRaw,
    apartmentListRaw,
  ]);

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="bg-background pt-14 pb-4 md:pt-20 md:pb-6 lg:pt-24">
      <div className="mx-auto flex max-w-7xl flex-col gap-16 px-4 sm:px-6 md:gap-24 lg:px-8">
        {communityItems.length > 0 && (
          <AmenityBand
            heading={(
              <EditableToken path="amenitieslist.communityHeading" as="h2" className="amenities-band-heading">
                {communityHeading || 'Community Amenities'}
              </EditableToken>
            )}
            items={communityItems}
            itemsBinding="amenitieslist.communityItems"
            itemsLabel="Community Amenities"
            photos={communityPhotos}
            photosBinding="amenitieslist.communityPhotos"
            photosLabel="Community Photos"
            flipped={false}
          />
        )}
        {apartmentItems.length > 0 && (
          <AmenityBand
            heading={(
              <EditableToken path="amenitieslist.apartmentHeading" as="h2" className="amenities-band-heading">
                {apartmentHeading || 'Apartment Features'}
              </EditableToken>
            )}
            items={apartmentItems}
            itemsBinding="amenitieslist.apartmentItems"
            itemsLabel="Apartment Features"
            photos={apartmentPhotos}
            photosBinding="amenitieslist.apartmentPhotos"
            photosLabel="Apartment Photos"
            flipped
          />
        )}
        {!hasContent && (
          <p className="py-12 text-center font-body text-lg text-muted-foreground">Amenities will be displayed here.</p>
        )}
      </div>

      <style>{`
        .amenities-band {
          display: grid;
          align-items: start;
          gap: 1.75rem;
        }
        @media (min-width: 992px) {
          .amenities-band {
            grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
            gap: 3.5rem;
            align-items: stretch;
          }
          .amenities-band--flip {
            grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
          }
          .amenities-band--flip .amenities-band-copy {
            order: -1;
          }
          .amenities-band-copy {
            position: relative;
            min-height: 100%;
          }
          .amenities-band-copy-inner {
            position: absolute;
            inset: 0;
            display: flex;
            flex-direction: column;
            overflow: auto;
          }
        }
        @media (min-width: 1200px) {
          .amenities-band {
            gap: 4.5rem;
          }
        }
        @media (max-width: 991px) {
          .amenities-band-media {
            order: -1;
          }
          .amenities-band--flip .amenities-band-copy {
            order: 0;
          }
        }

        .amenities-band-heading {
          margin: 0 0 1.25rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.75rem, 2.4vw, 2.5rem);
          font-weight: 400;
          line-height: 1.15;
          color: var(--vibe-color-text);
        }

        .amenities-name-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .amenities-name-item {
          display: flex;
          align-items: flex-start;
          gap: 0.7rem;
          break-inside: avoid;
          padding: 0.4rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.98rem;
          line-height: 1.4;
          color: var(--vibe-color-text);
          border-bottom: 1px solid var(--vibe-color-border);
        }
        .amenities-name-mark {
          flex-shrink: 0;
          width: 6px;
          height: 6px;
          margin-top: 0.5em;
          border-radius: 50%;
          background: var(--vibe-color-primary);
        }

        .amenities-pagination {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 0.75rem;
          margin-top: auto;
          padding-top: 1rem;
        }
        .amenities-page-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--vibe-color-border);
          background: var(--vibe-color-background);
          color: var(--vibe-color-text);
          cursor: pointer;
        }
        .amenities-page-btn:disabled {
          opacity: 0.4;
          cursor: default;
        }
        .amenities-page-btn:focus-visible,
        .amenities-page-dot:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }
        .amenities-page-dots {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .amenities-page-dot {
          width: 8px;
          height: 8px;
          padding: 0;
          border-radius: 50%;
          border: 1.5px solid var(--vibe-color-border);
          background: transparent;
          cursor: pointer;
        }
        .amenities-page-dot--active {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }

        .amenities-carousel {
          position: relative;
          min-height: 20rem;
          height: 70vw;
          max-height: 36rem;
          overflow: hidden;
          background: var(--vibe-color-surface);
        }
        @media (min-width: 992px) {
          .amenities-carousel {
            height: 100%;
            min-height: 30rem;
            max-height: none;
          }
        }

        .amenities-carousel-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-carousel-img--active {
          opacity: 1;
          z-index: 1;
          pointer-events: auto;
        }

        .amenities-carousel-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 44px;
          height: 44px;
          border: 1px solid var(--vibe-color-border);
          background: var(--vibe-color-background);
          color: var(--vibe-color-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 2;
          box-shadow: var(--vibe-shadow-md, 0 4px 16px rgba(0, 0, 0, 0.12));
        }
        .amenities-carousel-arrow:hover {
          color: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }
        .amenities-carousel-arrow:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }
        .amenities-carousel-arrow--prev { left: 12px; }
        .amenities-carousel-arrow--next { right: 12px; }

        .amenities-carousel-dots {
          position: absolute;
          bottom: 14px;
          left: 50%;
          transform: translateX(-50%);
          z-index: 2;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 8px;
          max-width: calc(100% - 7rem);
        }
        .amenities-carousel-dot {
          width: 8px;
          height: 8px;
          padding: 0;
          border-radius: 50%;
          border: 1.5px solid var(--vibe-color-background);
          background: transparent;
          cursor: pointer;
          box-shadow: 0 0 0 1px color-mix(in srgb, var(--vibe-color-text) 35%, transparent);
        }
        .amenities-carousel-dot--active {
          background: var(--vibe-color-background);
        }
        .amenities-carousel-dot:focus-visible {
          outline: 2px solid var(--vibe-color-background);
          outline-offset: 2px;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-carousel-img {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'text',
      defaultValue: 'Apartment Features',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
